<script setup lang="ts">
import type { NavItem } from '@/components/UiNavigation/UiNavigation.types'
import { useInvestorSubMenu } from '@/domains/investor/composables/useInvestorSubMenu'
import { useRouteLink } from '@/composables/useRouteLink'

const { items: customItems } = defineProps<{ items?: NavItem[] }>()

const investorItems = useInvestorSubMenu()
const items = computed(() => customItems ?? investorItems.value)
const openElementId = ref('')
const { routeLink } = useRouteLink()

function toggle(name: string) {
  if (openElementId.value === name) {
    openElementId.value = ''
  } else {
    openElementId.value = name
  }
}

function enterKeyHandler(event: KeyboardEvent, name: string) {
  toggle(name)
  nextTick(() => {
    const firstChild = (event.target as HTMLElement)?.nextElementSibling
      ?.firstChild
    if (firstChild && openElementId) {
      ;(firstChild as HTMLElement)?.focus()
    }
  })
}

</script>

<template>
  <nav class="investor-submenu">
    <ul class="investor-submenu__list">
      <li
        v-for="(item, index) in items"
        :key="item.label"
        class="investor-submenu__item"
        tabindex="0"
        @keyup.enter="enterKeyHandler($event, `item-${index}`)"
        @mouseenter="toggle(`item-${index}`)"
        @mouseleave="toggle(`item-${index}`)"
      >
        <span
          v-if="item.children?.length"
          :class="[
            'investor-submenu__link',
            'investor-submenu__link--dropdown',
            { 'is-active': openElementId === `item-${index}` },
          ]"
        >
          {{ $t(item.label) }}
        </span>

        <NuxtLink
          v-else-if="$t(item.link) && !item.external"
          class="investor-submenu__link"
          :to="routeLink($t(item.link), undefined, item.linkToLocale)"
          >
          {{ $t(item.label) }}
        </NuxtLink>

        <a
          v-else-if="$t(item.link)"
          class="investor-submenu__link"
          :href="$t(item.link)"
        >
          {{ $t(item.label) }}
        </a>

        <span
          v-else
          class="investor-submenu__link"
        >
          {{ $t(item.label) }}
        </span>

        <transition name="dropdown">
          <ul
            v-if="item.children?.length"
            v-show="openElementId === `item-${index}`"
            class="investor-submenu__inner"
          >
            <li
              v-for="child in item.children"
              :key="child.label"
              class="investor-submenu__inner_item"
            >
              <NuxtLink
                v-if="$t(child.link) && !child.external"
                class="investor-submenu__inner_link"
                :to="routeLink($t(child.link), undefined, child.linkToLocale)"
              >
                {{ $t(child.label) }}
              </NuxtLink>

              <a
                v-else-if="$t(child.link) && child.external"
                class="investor-submenu__inner_link"
                :href="$t(child.link)"
                target="_blank"
              >
                {{ $t(child.label) }}
              </a>
              
              <span
                v-else
                class="investor-submenu__inner_link"
              >
                {{ $t(child.label) }}
              </span>
            </li>
          </ul>
        </transition>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
.investor-submenu {
  $root: &;

  position: fixed;
  top: calc(8.6rem - 1px);
  right: 0;
  left: 0;
  z-index: 90;
  display: none;
  padding-top: 1px;
  background-color: $gray-100;

  @include atM {
    display: block;
  }

  &__list {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3.2rem;
    padding: 3.2rem 4.8rem;
  }

  &__item {
    position: relative;

    &:hover {
      > #{$root}__link {
        color: $purple-400;

        &#{$root}__link--dropdown:after {
          background-image: url('/icons/arrow_down_purple.svg');
        }
      }
    }
  }

  &__link {
    @include body-3;
    color: $text-400;
    white-space: nowrap;
    cursor: pointer;

    &--dropdown {
      &:after {
        display: inline-block;
        width: 1.6rem;
        height: 1.6rem;
        margin-left: 0.4rem;
        vertical-align: middle;
        background-image: url('/icons/arrow_down.svg');
        background-position: center;
        background-size: cover;
        transition: transform 0.25s ease;
        content: '';
      }

      &.is-active:after {
        transform: rotate(180deg);
      }
    }
  }

  &__inner {
    position: absolute;
    top: calc(100% + 3.2rem);
    left: -2.4rem;
    z-index: 9;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    width: 30.2rem;
    padding: 1.6rem;
    background-color: $white;
    border-radius: 1.2rem;
    box-shadow: 0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08);

    // invisible bridge over the bar padding, so hover survives the gap
    &:before {
      position: absolute;
      top: -3.2rem;
      left: 0;
      width: 100%;
      height: 3.2rem;
      content: '';
    }

    &:after {
      position: absolute;
      top: -0.7rem;
      left: 4rem;
      z-index: -1;
      width: 1.6rem;
      height: 1.6rem;
      background-color: $white;
      border-radius: 0.2rem;
      transform: rotate(45deg);
      content: '';
    }

    &_link {
      @include body-3;
      display: block;
      padding: 0.8rem;
      color: $text-400;
      border-radius: 0.8rem;

      &:hover {
        background-color: $gray-200;
      }
    }
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  transform: translateY(-0.8rem);
  opacity: 0;
}
</style>
